// ---------------------------------------------------------------------------
// YouAreYou (uru) — EngagedViewPage
//
// Simplified KYC warm-handoff page for Simple Mode verification links.
// Route: /engage/:uuid (public — no auth required)
//
// A single centered card showing:
//   - Gov ID + Liveness Scan requirements only
//   - Requester name from ?name= query param (falls back to "Someone")
//   - "Verify My Identity" CTA → hard nav to MojoVerify
//
// After KYC, MojoVerify redirects to the original connection link (/c/:uuid
// or /v/:code) which shows the verified status on the connection page.
//
// Design: minimal, single-column card. No split panel.
// Sister: src/pages/public/StartVerificationPage.tsx (full split-panel version)
// ---------------------------------------------------------------------------

import { useParams, useSearchParams, Link } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import BrandName from "@/components/ui/BrandName";
import BrandMark from "@/components/ui/BrandMark";

const MOJO_BASE = import.meta.env.VITE_API_BASE_URL as string;

// ---------------------------------------------------------------------------
// The two credentials required in Simple Mode
// ---------------------------------------------------------------------------

const SIMPLE_CREDENTIALS = [
  { icon: "badge", label: "Government ID", description: "A photo of your government-issued ID" },
  { icon: "face", label: "Liveness Scan", description: "A short selfie video to confirm you're present" },
];

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header() {
  return (
    <header className="border-b border-slate-200 bg-white px-6 py-4">
      <div className="mx-auto flex max-w-7xl items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark className="h-8 w-8" />
          <span className="text-xl font-black tracking-tight">
            <BrandName />
          </span>
        </Link>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function Footer() {
  return (
    <footer className="border-t border-slate-200 py-8 text-center">
      <div className="mx-auto max-w-7xl px-6">
        <p className="text-sm text-slate-500">
          Secure identity verification by <BrandName />
        </p>
        <div className="mt-3 flex justify-center gap-6 text-xs text-slate-400">
          <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
          <Link to="/terms" className="hover:underline">Terms of Service</Link>
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// EngagedViewPage
// ---------------------------------------------------------------------------

export default function EngagedViewPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const [searchParams] = useSearchParams();

  const requesterName = searchParams.get("name")?.trim() || "Someone";
  const verifyUrl = `${MOJO_BASE}/verify/basic/${uuid ?? ""}`;

  function handleStart() {
    window.location.href = verifyUrl;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 font-sans text-slate-900">
      <Header />

      <main className="flex flex-1 flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Requester context */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50">
              <Icon name="verified_user" filled className="text-3xl text-primary-500" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
              {requesterName === "Someone"
                ? "You've been asked to verify your identity"
                : `${requesterName} wants to verify your identity`}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              This takes about 2 minutes. Your documents are never shared with the requester.
            </p>
          </div>

          {/* Card */}
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50">
            {/* What you'll need */}
            <div className="border-b border-slate-100 px-6 py-4">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                What you'll need
              </p>
            </div>

            <div className="divide-y divide-slate-100">
              {SIMPLE_CREDENTIALS.map((cred) => (
                <div key={cred.label} className="flex items-center gap-4 px-6 py-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50">
                    <Icon name={cred.icon} filled className="text-xl text-primary-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{cred.label}</p>
                    <p className="text-xs text-slate-500">{cred.description}</p>
                  </div>
                  <span className="rounded-full bg-primary-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-primary-600">
                    Required
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="px-6 pb-6 pt-2">
              <button
                onClick={handleStart}
                className="mt-4 flex w-full items-center justify-center gap-3 rounded-2xl bg-primary-500 px-6 py-4 text-base font-bold text-white shadow-lg shadow-primary-500/25 transition-all hover:bg-primary-600 hover:shadow-xl active:scale-[0.98]"
              >
                <span>Verify My Identity</span>
                <Icon name="arrow_forward" className="text-lg" />
              </button>
              <p className="mt-3 text-center text-xs text-slate-400">
                Powered by MojoVerify · Your data is encrypted and never stored by <BrandName />
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
